import type { Preferences, ThemeId } from './types';

export interface ThemePreset {
  id: ThemeId;
  name: string;
  description: string;
  accent: string;
}

export const DEFAULT_THEME: ThemeId = 'liquid-light';

export const THEME_PRESETS: ThemePreset[] = [
  {
    id: 'focus-dark',
    name: 'Midnight Focus',
    description: 'Soft charcoal and sage for low light.',
    accent: '#b0c9a4',
  },
  {
    id: 'liquid-light',
    name: 'Liquid Light',
    description: 'Quiet stone surfaces with a muted green accent.',
    accent: '#365e4c',
  },
  {
    id: 'graphite',
    name: 'Graphite',
    description: 'Low-chroma neutral contrast with precise controls.',
    accent: '#bac6de',
  },
  {
    id: 'solar',
    name: 'Solar',
    description: 'Warm paper tones with a soft brown accent.',
    accent: '#76502f',
  },
  {
    id: 'aurora',
    name: 'Aurora',
    description: 'Deep teal surfaces with a soft mint accent.',
    accent: '#9bcfc5',
  },
];

const THEME_IDS = new Set<ThemeId>(THEME_PRESETS.map((theme) => theme.id));

export const isThemeId = (value: unknown): value is ThemeId =>
  typeof value === 'string' && THEME_IDS.has(value as ThemeId);

export const normalizeTheme = (value: unknown): ThemeId => {
  if (isThemeId(value)) return value;
  if (value === 'light') return 'liquid-light';
  if (value === 'dark' || value === 'system') return DEFAULT_THEME;
  return DEFAULT_THEME;
};

export const normalizePreferences = (value: unknown): Preferences => {
  if (!value || typeof value !== 'object') return { theme: DEFAULT_THEME };
  return { theme: normalizeTheme((value as { theme?: unknown }).theme) };
};
